import { computeUnitPrice } from '@/features/menu/computeUnitPrice';
import type { ConsumerMode } from '@/features/menu/resolveModePrice';
import { catalogName } from '@/lib/localizeCatalog';
import type { CartLine, Menu } from '@/types';

export interface RepricedLine {
  lineId: string;
  /** Fresh price in cents, options included. */
  unitPrice: number;
  /** Product name, for the "prices changed" notice. */
  name: string;
}

/**
 * Reconcile the frozen cart-line prices against a freshly fetched menu and
 * report the lines that moved. Pure — the caller dispatches updateLineUnitPrice
 * and decides how to surface the change.
 *
 * SC-524: shared by CartDrawer (on open) and CheckoutPage (on mount) so the two
 * surfaces can never disagree on a price. They did before: the drawer showed the
 * price frozen at add-time while the checkout page — and the order — carried the
 * back-office's new one.
 */
export function repriceCartLines(
  lines: CartLine[],
  menu: Menu | undefined,
  isBuffetDineIn: boolean,
  mode: ConsumerMode,
): RepricedLine[] {
  if (!menu || lines.length === 0) return [];
  const changed: RepricedLine[] = [];
  for (const line of lines) {
    const item = menu.items.find((it) => it.id === line.itemId);
    if (!item) continue;
    // Per-product resolved groups (per-product price + is_active); fall back
    // to the shared list only for older menus that lack item.optionGroups.
    // Using the flat list here collapses a shared group to the first product's
    // price → wrong unitPrice → total_mismatch at submit. (SC-97, mirrors SC-93)
    const groups =
      item.optionGroups ??
      item.optionGroupIds
        .map((gid) => menu.optionGroups.find((g) => g.id === gid))
        .filter((g): g is NonNullable<typeof g> => Boolean(g));
    // SC-250: re-price against the mode that will actually be submitted
    // (server quotes this same mode), so the displayed total matches the charge.
    const fresh = computeUnitPrice(item, groups, line.selectedOptions, isBuffetDineIn, mode);
    // A menu entry that came back without a usable price yields NaN. Overwriting
    // a real cart price with it would put the customer on a broken total and
    // guarantee total_mismatch at submit — leave the line alone instead.
    if (!Number.isFinite(fresh)) continue;
    if (fresh !== line.unitPrice) {
      changed.push({ lineId: line.lineId, unitPrice: fresh, name: catalogName(item) });
    }
  }
  return changed;
}
